import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCloudUploadAlt, 
    faImage, 
    faFilePdf,
    faTimes, 
    faCheck,
    faSpinner,
    faTrash
} from '@fortawesome/free-solid-svg-icons';
import api from '../../api/api';
import './ImageUploader.scss';

const ImageUploader = ({ 
    onUploadSuccess, 
    onUploadError,
    folder = 'general',
    entityId = null,
    entityType = null, // 'aircraft', 'event', 'tour', 'pilot'
    acceptPdf = false,
    multiple = false,
    currentImageUrl = null,
    showPreview = true,
    maxSize = 10 // MB
}) => {
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [preview, setPreview] = useState(currentImageUrl);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const fileInputRef = useRef(null);

    const acceptedTypes = acceptPdf 
        ? 'image/jpeg,image/jpg,image/png,image/webp,image/gif,application/pdf'
        : 'image/jpeg,image/jpg,image/png,image/webp,image/gif';

    const validateFile = (file) => {
        // Validar tipo
        const validTypes = acceptPdf 
            ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
            : ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
        
        if (!validTypes.includes(file.type)) {
            onUploadError && onUploadError(`Invalid file type. Allowed: ${validTypes.join(', ')}`);
            return false;
        }

        // Validar tamaño
        if (file.size > maxSize * 1024 * 1024) {
            onUploadError && onUploadError(`File size exceeds ${maxSize}MB`);
            return false;
        }

        return true;
    };

    const handleFileSelect = (files) => {
        const fileArray = Array.from(files);
        
        if (!multiple && fileArray.length > 1) {
            onUploadError && onUploadError('Only one file allowed');
            return;
        }

        const validFiles = fileArray.filter(validateFile);
        
        if (validFiles.length > 0) {
            setSelectedFiles(validFiles);
            
            // Preview para imágenes
            if (showPreview && validFiles[0].type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreview(reader.result);
                };
                reader.readAsDataURL(validFiles[0]);
            } else if (validFiles[0].type === 'application/pdf') {
                setPreview('pdf');
            }
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const uploadFiles = async () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);
        setUploadProgress(0);
        setUploadSuccess(false);

        try {
            const formData = new FormData();
            
            if (multiple) {
                selectedFiles.forEach(file => {
                    formData.append('images', file);
                });
            } else {
                // Always use 'file' for single uploads
                formData.append('file', selectedFiles[0]);
            }

            // Determinar el endpoint según el tipo de entidad
            let endpoint = '/upload/single';
            if (multiple) {
                endpoint = '/upload/multiple';
            } else if (entityType && entityId) {
                endpoint = `/upload/${entityType}/${entityId}`;
            }

            // Agregar folder como query param si no es una entidad específica
            const url = (!entityType || !entityId) 
                ? `${endpoint}?folder=${folder}`
                : endpoint;

            const response = await api.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            setUploadSuccess(true);
            setUploadProgress(100);
            
            // Limpiar después de un tiempo
            setTimeout(() => {
                setSelectedFiles([]);
                setUploadProgress(0);
                setUploadSuccess(false);
                if (!currentImageUrl) {
                    setPreview(null);
                }
            }, 2000);

            onUploadSuccess && onUploadSuccess(response.data);
        } catch (error) {
            console.error('Upload error:', error);
            onUploadError && onUploadError(
                error.response?.data?.error || 'Failed to upload file'
            );
            setUploadProgress(0);
        } finally {
            setUploading(false);
        }
    };

    const deleteCurrentImage = async () => {
        if (!currentImageUrl || !window.confirm('Are you sure you want to delete this image?')) {
            return;
        }

        try {
            await api.delete('/upload/delete', {
                data: { url: currentImageUrl }
            });
            
            setPreview(null);
            onUploadSuccess && onUploadSuccess({ deleted: true });
        } catch (error) {
            console.error('Delete error:', error);
            onUploadError && onUploadError('Failed to delete image');
        }
    };

    const clearSelection = () => {
        setSelectedFiles([]);
        setPreview(currentImageUrl);
        setUploadProgress(0);
        setUploadSuccess(false);
    };

    return (
        <div className="image-uploader">
            {showPreview && preview && (
                <div className="preview-container">
                    {preview === 'pdf' ? (
                        <div className="pdf-preview">
                            <FontAwesomeIcon icon={faFilePdf} />
                            <span>PDF Document</span>
                        </div>
                    ) : (
                        <img src={preview} alt="Preview" className="preview-image" />
                    )}
                    {currentImageUrl && (
                        <button 
                            className="delete-button"
                            onClick={deleteCurrentImage}
                            title="Delete current image"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            )}

            <div 
                className={`upload-area ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !uploading && fileInputRef.current?.click()}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedTypes}
                    multiple={multiple}
                    onChange={(e) => handleFileSelect(e.target.files)}
                    style={{ display: 'none' }}
                />

                {uploading ? (
                    <div className="upload-progress">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <div className="progress-bar">
                            <div 
                                className="progress-fill"
                                style={{ width: `${uploadProgress}%` }}
                            />
                        </div>
                        <span>{uploadProgress}%</span>
                    </div>
                ) : uploadSuccess ? (
                    <div className="upload-success">
                        <FontAwesomeIcon icon={faCheck} />
                        <span>Upload successful!</span>
                    </div>
                ) : selectedFiles.length > 0 ? (
                    <div className="files-selected">
                        <p>{selectedFiles.length} file(s) selected</p>
                        <div className="file-list">
                            {selectedFiles.map((file, index) => (
                                <div key={index} className="file-item">
                                    <FontAwesomeIcon 
                                        icon={file.type === 'application/pdf' ? faFilePdf : faImage} 
                                    />
                                    <span>{file.name}</span>
                                </div>
                            ))}
                        </div>
                        <div className="upload-actions">
                            <button 
                                className="btn-upload"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    uploadFiles();
                                }}
                            >
                                Upload
                            </button>
                            <button 
                                className="btn-clear"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearSelection();
                                }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="upload-prompt">
                        <FontAwesomeIcon icon={faCloudUploadAlt} />
                        <p>Drag & drop files here or click to browse</p>
                        <span className="file-types">
                            {acceptPdf ? 'Images (JPG, PNG, WebP, GIF) or PDF' : 'Images (JPG, PNG, WebP, GIF)'}
                        </span>
                        <span className="file-size">Max size: {maxSize}MB</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageUploader;